import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Reviews', path: '/reviews' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top bar */}
      <div className="hidden lg:block bg-slate-900 text-white py-2">
        <div className="container flex justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            <a href="tel:+12363380659" className="flex items-center gap-2 hover:text-blue-400 transition-colors" data-testid="top-bar-phone">
              <Phone className="w-4 h-4" />
              <span>(236) 338-0659</span>
            </a>
            <a href="mailto:info@ssgroupservices.ca" className="flex items-center gap-2 hover:text-blue-400 transition-colors" data-testid="top-bar-email">
              <Mail className="w-4 h-4" />
              <span>info@ssgroupservices.ca</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-slate-400">Licensed & Insured</span>
            <span className="text-slate-400">|</span>
            <span className="text-slate-400">Serving British Columbia</span>
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <nav 
        className={`sticky-header ${isScrolled ? 'scrolled bg-white shadow-lg' : 'bg-white'}`}
        data-testid="main-navbar"
      >
        <div className="container">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_ss-build-preview/artifacts/jzohyi1e_CL%20S%26S%20GROUP%20SERVICES%20CREATION%20COMPANY.png" 
                alt="S&S Group Services Construction" 
                className="h-16 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-semibold uppercase tracking-wider transition-colors ${
                    isActive(link.path) 
                      ? 'text-blue-600' 
                      : 'text-slate-700 hover:text-blue-600'
                  }`}
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hidden lg:flex items-center gap-4">
              <a href="tel:+12363380659">
                <Button variant="outline" className="font-bold uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="navbar-call-btn">
                  <Phone className="w-4 h-4 mr-2" />
                  Call Now
                </Button>
              </a>
              <Link to="/contact">
                <Button className="bg-blue-600 hover:bg-blue-700 font-bold uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="navbar-quote-btn">
                  Get a Quote
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex lg:hidden items-center gap-4">
              <a href="tel:+12363380659" className="bg-blue-600 text-white p-2 rounded" data-testid="mobile-call-btn">
                <Phone className="w-5 h-5" />
              </a>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-slate-900 p-2"
                data-testid="mobile-menu-toggle"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div 
          className={`lg:hidden absolute top-full left-0 right-0 bg-white shadow-lg transform transition-all duration-300 ${
            isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
          data-testid="mobile-menu"
        >
          <div className="container py-6">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-lg font-semibold uppercase tracking-wider py-2 ${
                    isActive(link.path) 
                      ? 'text-blue-600' 
                      : 'text-slate-700'
                  }`}
                  style={{ fontFamily: 'Oswald, sans-serif' }}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-slate-200">
                <Link to="/contact">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700 font-bold uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }} data-testid="mobile-get-quote-btn">
                    Get a Free Quote
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;
